import { initContract } from "@ts-rest/core";
import { z } from "zod";
import { ErrorMessageSchema } from "@common/schemas/error.schema";
import { OrganizationIdNameLogoUrlArraySchema } from "@common/schemas/organization.schema";
import { WorkspaceMemberDto } from "../dto/workspace/workspace-member.dto";
import { assignableWorkspaceRoleSchema } from "../userWorkspaceRoles";
import { WorkspaceInviteDto } from "../dto/workspace/workspace-invite.dto";

const c = initContract();

export const workspaceMemberContract = c.router(
    {
        getMembers: {
            method: "GET",
            path: "/",
            responses: {
                200: c.type<WorkspaceMemberDto[]>(),
            },
        },
        getMember: {
            method: "GET",
            path: "/member/:memberId",
            pathParams: z.object({
                memberId: z.coerce.number(),
            }),
            responses: {
                200: c.type<WorkspaceMemberDto>(),
            },
        },
        removeMember: {
            method: "DELETE",
            path: "/:memberId",
            pathParams: z.object({
                memberId: z.coerce.number(),
            }),
            responses: {
                200: c.noBody(),
            },
        },
        removeOrganizationMembers: {
            method: "DELETE",
            path: "/organization/:organizationIdToRemove",
            pathParams: z.object({
                organizationIdToRemove: z.string().uuid(),
            }),
            responses: {
                200: c.noBody(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
        updateWorkspaceAssignment: {
            method: "PATCH",
            path: "/:memberId",
            pathParams: z.object({
                memberId: z.coerce.number(),
            }),
            body: z.object({
                role: assignableWorkspaceRoleSchema,
                entityAssignments: z.array(z.number()),
                assignToNewEntities: z.boolean(),
            }),
            responses: {
                200: c.noBody(),
            },
        },
        inviteMember: {
            method: "POST",
            path: "/invite",
            body: z.object({
                emails: z.array(z.string().email()),
                role: assignableWorkspaceRoleSchema,
                entityAssignments: z.array(z.number()),
                assignToNewEntities: z.boolean(),
            }),
            responses: {
                200: c.type<WorkspaceInviteDto[]>(),
            },
        },
        checkOrganizationInvites: {
            method: "POST",
            path: "/invite/check-organizations",
            body: z.object({
                emails: z.array(z.string().email()),
            }),
            responses: {
                200: OrganizationIdNameLogoUrlArraySchema,
            },
        },
        getInvites: {
            method: "GET",
            path: "/invite",
            responses: {
                200: c.type<WorkspaceInviteDto[]>(),
            },
        },
        resendInvite: {
            method: "POST",
            path: "/invite/:inviteId/resend",
            pathParams: z.object({
                inviteId: z.coerce.number(),
            }),
            body: c.noBody(),
            responses: {
                200: c.noBody(),
            },
        },
        cancelInvite: {
            method: "DELETE",
            path: "/invite/:inviteId",
            pathParams: z.object({
                inviteId: z.coerce.number(),
            }),
            responses: {
                200: c.noBody(),
            },
        },
        transferPrimaryAdmin: {
            method: "POST",
            path: "/:memberId/transfer-primary-admin",
            pathParams: z.object({
                memberId: z.coerce.number(),
            }),
            body: c.noBody(),
            responses: {
                200: c.noBody(),
                400: ErrorMessageSchema,
                403: ErrorMessageSchema,
                404: ErrorMessageSchema,
            },
        },
    },
    {
        pathPrefix: "/workspace-member/:workspaceId",
    },
);
